<script setup lang="ts">
import { useDefaultStore } from "@/store";
import api from "@/api";
import type { IPost, IStatus, ITag } from "@/api/types";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { useSessionStore } from "@/store/session";

definePage({
	name: "EditPost",
	meta: {
		name: "Edit Post",
	},
});

const store = useDefaultStore();
const toast = useToast();
const route = useRoute();
const router = useRouter();
const sessionStore = useSessionStore();
const post = ref<IPost | null>(null);
const localeDate = ref<Date | Date[] | null>(null);
const postStatus = ref<IStatus | null>(null);
const newThumbnailFile = ref<File | null>(null);
const newPosterFile = ref<File | null>(null);
const postThumbnailUrl = ref<string | null>(null);
const postPosterUrl = ref<string | null>(null);
const submitLoading = ref(false);
const currentUserId = ref(-1);
const tagIds = ref([]);
const postTags = ref<ITag[] | null>();

async function getIdFromPath() {
	const params = route.params as { id: string | string[] };

	if (Array.isArray(params.id)) {
		const id = parseInt(params.id[0], 10);
		if (!isNaN(id) && id > 0) {
			currentUserId.value = id;
			await getPosts(id);
		}
	} else {
		const id = parseInt(params.id, 10);
		if (!isNaN(id) && id > 0) {
			currentUserId.value = id;
			await getPosts(id);
		}
	}
}

async function getPosts(pst_id) {
	const params = {
		pst_id: pst_id,
		draft: true,
		hidden: true,
		scheduled: true,
		admin_panel: true,
	};

	try {
		const res = await api.postsGet(params);
		if (
			res.data.data &&
			res.data.data.length > 0 &&
			res.data.data[0].pst_id !== null
		) {
			post.value = res.data.data[0];
		} else {
			router.push({ name: "EditPosts" });
		}

		if (post.value) {
			localeDate.value = new Date(
				store.utcToLocale(post.value.pst_utc_datetime, true),
			);

			const foundStatus = store.statuses.find(
				(status) => status.code === post.value.sta_code,
			);

			if (foundStatus) {
				postStatus.value = foundStatus;
			} else {
				postStatus.value = store.statuses[1];
			}

			postTags.value = JSON.parse(post.value.tags);
			if (postTags.value)
				tagIds.value = postTags.value.map((tag) => tag.tag_id);

			postPosterUrl.value = post.value.poster;
			postThumbnailUrl.value = post.value.thumbnail;
		}
	} catch (err: any) {
		console.warn(err.message);
	}
}

function onImageSelect($event, type: string) {
	const file = $event.files[0];

	if (file.size > 1500000) {
		toast.add({
			severity: "warn",
			summary: $t("app.postForm.warn.imageSize.title"),
			detail: $t("app.postForm.warn.imageSize.msg"),
			life: 3000,
		});
	}

	const previewUrl = URL.createObjectURL(file);

	if (type === "poster") {
		post.value.poster = previewUrl;
		newPosterFile.value = file;
	} else if (type === "thumbnail") {
		post.value.thumbnail = previewUrl;
		newThumbnailFile.value = file;
	}
}

function onImageClear(type: string) {
	if (type === "poster") {
		post.value.poster = postPosterUrl.value;
	} else if (type === "thumbnail") {
		post.value.thumbnail = postThumbnailUrl.value;
	}
}

async function editPost() {
	if (!sessionStore.can("edit-post")) {
		toast.add({
			severity: "error",
			summary: $t("app.permissions.editPost.title"),
			detail: $t("app.permissions.editPost.msg"),
			life: 3000,
		});
		return;
	}

	if (!post.value.pst_title || !post.value.pst_title.trim()) {
		toast.add({
			severity: "error",
			summary: $t("app.postForm.error.requiredTitle.title"),
			detail: $t("app.postForm.error.requiredTitle.msg"),
			life: 3000,
		});
		return;
	}

	if (!post.value.pst_content || !post.value.pst_content.trim()) {
		toast.add({
			severity: "error",
			summary: $t("app.postForm.error.requiredContent.title"),
			detail: $t("app.postForm.error.requiredContent.msg"),
			life: 3000,
		});
		return;
	}
	submitLoading.value = true;

	const formData = new FormData();

	formData.append("pst_id", post.value.pst_id.toString());
	formData.append("pst_title", post.value.pst_title);
	formData.append("pst_description", post.value.pst_description);
	formData.append("pst_content", post.value.pst_content);

	const dateString = ref("");
	dateString.value = localeDate.value.toString();

	formData.append(
		"pst_utc_datetime",
		store.formatDatepickerDate(dateString.value),
	);

	formData.append("tag_ids", JSON.stringify(tagIds.value));
	formData.append("sta_code", postStatus.value.code);
	formData.append("poster_name", post.value.poster_name);

	if (post.value.poster !== postPosterUrl.value) {
		formData.append("poster", newPosterFile.value);
	}

	if (post.value.thumbnail !== postThumbnailUrl.value) {
		formData.append("thumbnail", newThumbnailFile.value);
	}

	try {
		const res = await api.postsEdit(formData);
		toast.add({
			severity: "success",
			summary: $t("app.editPost.success.title"),
			detail: $t("app.editPost.success.msg"),
			life: 3000,
		});
		// await getPosts(currentUserId.value);
		console.warn(res.data);
	} catch (err: any) {
		if (err.response.data.message.includes("Post not found.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.errorMsg.title"),
				detail: $t("app.editPost.errorMsg.postNotFound"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("Token has been blacklisted.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Unauthorized")) {
			toast.add({
				severity: "error",
				summary: $t("app.permissions.editPost.title"),
				detail: $t("app.permissions.editPost.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Title must be unique.")) {
			toast.add({
				severity: "error",
				summary: $t("app.editPost.error.invalidId.title"),
				detail: $t("app.editPost.error.invalidId.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Title is required.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.requiredTitle.title"),
				detail: $t("app.postForm.error.requiredTitle.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Content is required.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.requiredContent.title"),
				detail: $t("app.postForm.error.requiredContent.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Invalid image.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.invalidImage.title"),
				detail: $t("app.postForm.error.invalidImage.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("No update params.")) {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.noUpdateParams.title"),
				detail: $t("app.postForm.error.noUpdateParams.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	} finally {
		submitLoading.value = false;
	}
}

const isOptionDisabled = (option: IStatus): boolean => {
	if (!option.permission_create) return false;
	return !sessionStore.can(option.permission_create);
};

function onChangeTag(event: ITag[]) {
	if (event) {
		tagIds.value = event.map((tag) => tag.tag_id);
	} else {
		tagIds.value = [];
	}
	console.log(tagIds.value);
}

onMounted(async () => {
	getIdFromPath();
});
</script>

<template>
	<div class="create-post-wrapper admin-section-outer-wrapper">
		<div
			v-if="post"
			class="admin-section-inner-wrapper"
		>
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.editPost.title") }}
				</div>
			</div>
			<div class="admin-section-content-wrapper">
				<div class="admin-section-content-input-wrapper">
					<label
						for="title"
						class="admin-section-content-input-label"
					>
						{{ $t("app.postForm.title") }}*
					</label>
					<InputText
						id="title"
						maxlength="255"
						v-model="post.pst_title"
					/>
				</div>
				<div class="admin-section-content-input-wrapper">
					<label
						for="description"
						class="admin-section-content-input-label"
					>
						{{ $t("app.postForm.metaDescription") }}
					</label>
					<InputText
						id="description"
						maxlength="255"
						v-model="post.pst_description"
					/>
				</div>
				<div class="admin-section-content-form-group-wrapper">
					<div class="admin-section-content-form-group-left">
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="datepicker-24h"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.postDate") }}
							</label>
							<DatePicker
								id="datepicker-24h"
								showTime
								v-model="localeDate"
								hourFormat="24"
								fluid
								dateFormat="dd.mm.yy."
								showIcon
								iconDisplay="input"
							/>
						</div>
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="status"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.status") }}
							</label>
							<Select
								id="status"
								:options="store.statuses"
								optionLabel="name"
								v-model="postStatus"
								:optionDisabled="isOptionDisabled"
								fluid
							/>
						</div>
					</div>

					<div class="admin-section-content-form-group-left">
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="tags"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.tags.label") }}
							</label>
							<MultiSelect
								display="chip"
								v-model="postTags"
								:options="store.tagsList"
								optionLabel="tag_label"
								filter
								:placeholder="$t('app.postForm.tags.placeholder')"
								:maxSelectedLabels="10"
								:showClear="true"
								:resetFilterOnClear="true"
								:showToggleAll="true"
								:virtualScrollerOptions="{ itemSize: 36 }"
								@update:modelValue="onChangeTag($event)"
								fluid
							/>
						</div>
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="poster-name"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.posterName") }}
							</label>
							<InputText
								id="poster-name"
								maxlength="255"
								v-model="post.poster_name"
								fluid
							/>
						</div>
					</div>
				</div>
				<div class="admin-section-content-form-group-wrapper">
					<div class="admin-section-content-form-group-center">
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="poster"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.poster") }}
							</label>
							<FileUpload
								ref="posterUploadRef"
								name="poster"
								accept="image/*"
								:maxFileSize="5243000"
								:showUploadButton="false"
								:customUpload="true"
								:fileLimit="1"
								@select="onImageSelect($event, 'poster')"
								@clear="onImageClear('poster')"
								@remove="onImageClear('poster')"
								:chooseLabel="$t('app.basic.choose')"
								:cancelLabel="$t('app.basic.cancel')"
								:pt="{
									root: {
										style: 'margin-bottom: 15px',
									},
								}"
							>
								<template #empty>
									<span v-if="!store.isMobileSize">
										{{ $t("app.postForm.dragAndDrop") }}
									</span>
								</template>
							</FileUpload>
							<div
								v-if="post.poster"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.preview") }}
							</div>
							<Image
								v-if="post.poster"
								:src="post.poster"
								:alt="$t('app.postForm.poster')"
								:pt="{
									root: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;`,
									},
									image: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;
												object-fit: cover;`,
									},
								}"
							/>
						</div>
						<div class="admin-section-content-form-group-input-wrapper">
							<label
								for="thumbnail"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.thumbnail") }}
							</label>
							<FileUpload
								ref="thumbnailUploadRef"
								name="thumbnail"
								accept="image/*"
								:maxFileSize="5243000"
								:fileLimit="1"
								:showUploadButton="false"
								@select="onImageSelect($event, 'thumbnail')"
								@clear="onImageClear('thumbnail')"
								@remove="onImageClear('thumbnail')"
								:chooseLabel="$t('app.basic.choose')"
								:cancelLabel="$t('app.basic.cancel')"
								:pt="{
									root: {
										style: 'margin-bottom: 15px',
									},
								}"
							>
								<template #empty>
									<span v-if="!store.isMobileSize">
										{{ $t("app.postForm.dragAndDrop") }}
									</span>
								</template>
							</FileUpload>
							<div
								v-if="post.thumbnail"
								class="admin-section-content-form-group-input-label"
							>
								{{ $t("app.postForm.preview") }}
							</div>
							<Image
								v-if="post.thumbnail"
								:src="post.thumbnail"
								:alt="$t('app.postForm.thumbnail')"
								:pt="{
									root: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;`,
									},
									image: {
										style: `width: 100%;
												aspect-ratio: 100 / 55.55;
												object-fit: cover;`,
									},
								}"
							/>
						</div>
					</div>
				</div>
				<div class="admin-section-content-input-wrapper">
					<label
						for="content"
						class="admin-section-content-input-label"
					>
						{{ $t("app.postForm.content") }}*
					</label>
					<Editor
						contentType="html"
						v-model="post.pst_content"
					/>
				</div>
				<div class="admin-section-content-submit-wrapper">
					<Button
						class="admin-section-content-submit-button"
						:label="$t('app.basic.submit')"
						:loading="submitLoading"
						@click="editPost()"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-fileupload-file-badge {
	display: none !important;
}
.p-progressbar-determinate {
	display: none !important;
}
</style>
